/**
 * Parti etiketi — A4 dikey kurumsal çıktı (eski adı "Palet Etiketi").
 *
 * Ayrı bir yazdırma penceresi açar (SPA'nın CSS'inden bağımsız), bu yüzden
 * burada token (var(--…)) yerine literal marka renkleri kullanılır — standalone
 * print dökümanı uygulama stillerine erişemez. window.print() otomatik tetiklenir.
 *
 * Tasarım: İsviçre ızgarası / minimal kurumsal (tek-vurgu lacivert #14253E).
 * LOT numarası ayrıca Code128 barkodu olarak basılır (harici bağımlılık yok —
 * barkod SVG'si burada üretilir, taranabilir).
 */

export interface BatchLabelData {
  batchCode: string;
  itemName: string;
  itemCode: string;
  customerName: string;
  qty: number;
  uom: string;
  lotCode: string | null;
  locationName: string;
  producedAt: string; // ISO
  // Masthead: firma logosu + ünvanı (Ayarlar>Firma Bilgileri). Boşsa jenerik başlığa düşer.
  companyName?: string | null;
  companyLogo?: string | null; // mutlak URL
}

const esc = (s: string): string =>
  s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] ?? c));

/* ───────────────────────── Code 128 (B) barkod ─────────────────────────
 * LOT kodu alfanümerik (harf+rakam+tire) → Code 128B tüm yazdırılabilir
 * ASCII'yi (32–126) kapsar. Standart 107-desenli tablo + mod-103 sağlama.
 */
const CODE128_PATTERNS = [
  "212222", "222122", "222221", "121223", "121322", "131222", "122213", "122312", "132212", "221213",
  "221312", "231212", "112232", "122132", "122231", "113222", "123122", "123221", "223211", "221132",
  "221231", "213212", "223112", "312131", "311222", "321122", "321221", "312212", "322112", "322211",
  "212123", "212321", "232121", "111323", "131123", "131321", "112313", "132113", "132311", "211313",
  "231113", "231311", "112133", "112331", "132131", "113123", "113321", "133121", "313121", "211331",
  "231131", "213113", "213311", "213131", "311123", "311321", "331121", "312113", "312311", "332111",
  "314111", "221411", "431111", "111224", "111422", "121124", "121421", "141122", "141221", "112214",
  "112412", "122114", "122411", "142112", "142211", "241211", "221114", "413111", "241112", "134111",
  "111242", "121142", "121241", "114212", "124112", "124211", "411212", "421112", "421211", "212141",
  "214121", "412121", "111143", "111341", "131141", "114113", "114311", "411113", "411311", "113141",
  "114131", "311141", "411131", "211412", "211214", "211232", "2331112",
];
const START_B = 104;
const STOP = 106;

/** Bir dizgeyi Code128B modül genişlikleri dizgesine kodlar (sağlama dahil). */
function code128bModules(data: string): string {
  const values: number[] = [START_B];
  for (let i = 0; i < data.length; i++) {
    const code = data.charCodeAt(i);
    // Code128B aralığı dışındaki karakterleri boşlukla değiştir (güvenli sınır).
    values.push((code >= 32 && code <= 126 ? code : 32) - 32);
  }
  let sum = START_B; // başlangıç ağırlığı 1
  values.forEach((v, i) => { if (i > 0) sum += v * i; });
  values.push(sum % 103); // sağlama karakteri
  values.push(STOP);
  return values.map((v) => CODE128_PATTERNS[v] ?? "").join("");
}

/**
 * LOT kodu için taranabilir Code128 barkod SVG'si döndürür.
 * Sessiz alan (quiet zone) 10 modül her iki yanda — SVG'nin içinde, ölçekten bağımsız.
 * Kapsayıcıya yayılır (width:100%); modül oranları yatay ölçeklemede korunur.
 */
function barcodeSvg(lot: string): string {
  const QUIET = 10;
  const modules = code128bModules(lot);
  const total = QUIET * 2 + modules.split("").reduce((a, c) => a + Number(c), 0);
  const H = 60;
  let x = QUIET;
  let bars = "";
  let isBar = true; // ilk eleman çubuk
  for (const ch of modules) {
    const w = Number(ch);
    if (isBar) bars += `<rect x="${x}" y="0" width="${w}" height="${H}" fill="#18181B"/>`;
    x += w;
    isBar = !isBar;
  }
  return (
    `<svg viewBox="0 0 ${total} ${H}" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="LOT ${esc(lot)}">` +
    `<rect x="0" y="0" width="${total}" height="${H}" fill="#FFFFFF"/>${bars}</svg>`
  );
}

const CSS = `
  @page { size: A4 portrait; margin: 11mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html, body {
    font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    color: #18181B; background: #FFFFFF;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }
  .sw-mono { font-family: "JetBrains Mono", ui-monospace, "SF Mono", Menlo, Consolas, monospace; font-variant-numeric: tabular-nums; letter-spacing: -0.01em; }
  .sw-cap { font-size: 8.5px; font-weight: 600; letter-spacing: 0.16em; text-transform: uppercase; color: #71717A; }

  /* Ekran içi araç çubuğu (baskıda gizli) */
  .toolbar { position: sticky; top: 0; z-index: 5; display: flex; gap: 8px; justify-content: flex-end; padding: 10px 12px; background: #14253E; margin-bottom: 14px; }
  .toolbar button { font: inherit; font-size: 13px; font-weight: 600; cursor: pointer; border: 0; border-radius: 6px; padding: 8px 16px; }
  .toolbar .print { background: #fff; color: #14253E; }
  .toolbar .close { background: transparent; color: #cbd5e1; border: 1px solid #475569; }
  @media print { .toolbar { display: none; } }

  /* A4 belge çerçevesi */
  .sw-sheet {
    width: 100%; min-height: 272mm; margin: 0 auto;
    border: 1px solid #14253E; padding: 11mm 12mm 9mm;
    display: flex; flex-direction: column;
  }
  .sw-sheet * { box-sizing: border-box; }

  /* Masthead — SOL: firma logosu + ünvanı (sola yaslı) · SAĞ: belge kimliği */
  .sw-top { border-top: 2.5px solid #14253E; padding-top: 10px; display: flex; justify-content: space-between; align-items: flex-start; gap: 18px; }
  .sw-firm { display: flex; align-items: center; gap: 12px; min-width: 0; }
  .sw-logo { height: 15mm; max-width: 62mm; object-fit: contain; display: block; }
  .sw-firm-txt { min-width: 0; }
  .sw-firm-name { font-size: 20px; font-weight: 700; letter-spacing: 0.005em; color: #14253E; line-height: 1.1; word-break: break-word; }
  .sw-firm-sub { margin-top: 5px; font-size: 8.5px; font-weight: 600; letter-spacing: 0.20em; text-transform: uppercase; color: #71717A; }
  .sw-docid { text-align: right; padding: 0 0 6px 16px; border-left: 0.75px solid #D4D4D8; border-bottom: 2px solid #14253E; flex: none; }
  .sw-docid-title { font-size: 18px; font-weight: 700; letter-spacing: 0.10em; text-transform: uppercase; color: #14253E; line-height: 1; }
  .sw-docid-sub { margin-top: 4px; font-size: 7px; font-weight: 600; letter-spacing: 0.28em; text-transform: uppercase; color: #A1A1AA; }

  /* Parti kodu öne çıkan blok */
  .sw-batch { margin-top: 14px; border: 0.75px solid #D4D4D8; background: #EAEEF5; padding: 14px 18px; display: flex; align-items: center; justify-content: space-between; gap: 18px; }
  .sw-batch-l { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
  .sw-batch-code { font-size: 31px; font-weight: 700; letter-spacing: 0.01em; color: #0E1B30; line-height: 1; word-break: break-all; }
  .sw-batch-r { flex: none; text-align: right; padding-left: 18px; border-left: 0.75px solid #C7CDD9; }
  .sw-batch-r .sw-cap { color: #52525B; }
  .sw-batch-rv { margin-top: 5px; font-size: 15px; font-weight: 700; color: #14253E; }

  /* Ürün bloğu */
  .sw-item { margin-top: 14px; padding-bottom: 14px; border-bottom: 0.75px solid #E4E4E7; }
  .sw-item-head { display: flex; justify-content: space-between; align-items: baseline; }
  .sw-item-code { font-size: 13px; font-weight: 700; color: #14253E; }
  .sw-item-name { margin-top: 6px; font-size: 23px; font-weight: 700; line-height: 1.15; color: #18181B; letter-spacing: -0.005em; word-break: break-word; }

  /* 12-kolon bilgi ızgarası */
  .sw-grid { margin-top: 14px; display: grid; grid-template-columns: repeat(12, 1fr); border-top: 0.75px solid #18181B; border-left: 0.75px solid #E4E4E7; }
  .sw-cell { grid-column: span 4; padding: 11px 12px 12px; border-right: 0.75px solid #E4E4E7; border-bottom: 0.75px solid #E4E4E7; min-width: 0; }
  .sw-cell.sw-span12 { grid-column: span 12; }
  .sw-cell-v { margin-top: 6px; font-size: 16px; font-weight: 700; color: #18181B; line-height: 1.2; word-break: break-word; }
  .sw-cell-v .sw-uom { font-size: 11px; font-weight: 600; color: #71717A; margin-left: 5px; letter-spacing: 0.04em; }

  /* Alt zon: barkod (geniş) + imzalar (iki kolon), footer'a doğru yaslı */
  .sw-lower { margin-top: 16px; display: flex; flex-direction: column; flex: 1; }

  .sw-bc { border: 0.75px solid #14253E; border-radius: 4px; overflow: hidden; }
  .sw-bc-head { background: #14253E; color: #FFFFFF; padding: 9px 16px; display: flex; justify-content: space-between; align-items: center; }
  .sw-bc-head .sw-cap { color: #A1AABF; }
  .sw-bc-lot-top { font-size: 15px; font-weight: 700; color: #FFFFFF; letter-spacing: 0.02em; }
  .sw-bc-body { padding: 16px 18px 12px; background: #FFFFFF; display: flex; flex-direction: column; align-items: center; }
  .sw-bc-svg { width: 100%; max-width: 120mm; height: 19mm; padding: 0 9mm; }
  .sw-bc-svg svg { width: 100%; height: 100%; display: block; }
  .sw-bc-human { margin-top: 9px; font-size: 14px; font-weight: 700; letter-spacing: 0.08em; color: #18181B; }
  .sw-bc-empty { padding: 18px; text-align: center; font-size: 13px; color: #A1A1AA; }

  .sw-spacer { flex: 1; min-height: 12mm; }

  /* İmza alanları — iki kolon, dengeli */
  .sw-sign { display: grid; grid-template-columns: 1fr 1fr; gap: 18mm; }
  .sw-sign-line { border-top: 1px solid #18181B; padding-top: 7px; margin-top: 12mm; }
  .sw-sign-role { font-size: 12px; font-weight: 700; color: #14253E; letter-spacing: 0.02em; }
  .sw-sign-hint { margin-top: 3px; font-size: 8px; font-weight: 600; letter-spacing: 0.14em; text-transform: uppercase; color: #A1A1AA; }

  /* Footer */
  .sw-foot { margin-top: 14px; padding-top: 9px; border-top: 0.75px solid #14253E; display: flex; justify-content: space-between; align-items: center; font-size: 7.5px; font-weight: 600; letter-spacing: 0.10em; text-transform: uppercase; color: #A1A1AA; }
  .sw-foot .sw-foot-code { color: #14253E; letter-spacing: 0.02em; text-transform: none; }
`;

/** Etiketin tam HTML belgesini üretir (saf — yazdırma/pencere yan etkisi yok). */
export function buildBatchLabelHtml(d: BatchLabelData): string {
  const dt = new Date(d.producedAt);
  const dateStr = new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }).format(dt);
  const timeStr = new Intl.DateTimeFormat("tr-TR", { hour: "2-digit", minute: "2-digit" }).format(dt);
  const qtyStr = new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(d.qty);

  // Masthead: firma logosu + ünvanı (sola yaslı). Ünvan boşsa jenerik "STEPERP".
  const firmName = d.companyName && d.companyName.trim() ? esc(d.companyName) : "STEPERP";
  const logoHtml = d.companyLogo ? `<img class="sw-logo" src="${esc(d.companyLogo)}" alt="" />` : "";

  const lot = d.lotCode ?? null;
  const barcodeBlock = lot
    ? `<div class="sw-bc-svg">${barcodeSvg(lot)}</div>
        <div class="sw-bc-human sw-mono">${esc(lot)}</div>`
    : `<div class="sw-bc-empty">LOT numarası atanmamış</div>`;
  const lotTop = lot ? esc(lot) : "—";

  return `<!doctype html>
<html lang="tr">
<head>
<meta charset="utf-8" />
<title>Parti Etiketi — ${esc(d.batchCode)}</title>
<style>${CSS}</style>
</head>
<body>
  <div class="toolbar">
    <button class="print" onclick="window.print()" type="button">Yazdır</button>
    <button class="close" onclick="window.close()" type="button">Kapat</button>
  </div>

  <div class="sw-sheet">
    <header class="sw-top">
      <div class="sw-firm">
        ${logoHtml}
        <div class="sw-firm-txt">
          <div class="sw-firm-name">${firmName}</div>
          <div class="sw-firm-sub">Üretim Parti Takip Belgesi</div>
        </div>
      </div>
      <div class="sw-docid">
        <div class="sw-docid-title">Parti Etiketi</div>
        <div class="sw-docid-sub">Üretim Takip</div>
      </div>
    </header>

    <section class="sw-batch">
      <div class="sw-batch-l">
        <span class="sw-cap">Parti Kodu</span>
        <span class="sw-batch-code sw-mono">${esc(d.batchCode)}</span>
      </div>
      <div class="sw-batch-r">
        <span class="sw-cap">Üretim Tarihi</span>
        <div class="sw-batch-rv sw-mono">${dateStr}</div>
      </div>
    </section>

    <section class="sw-item">
      <div class="sw-item-head">
        <span class="sw-cap">Ürün</span>
        <span class="sw-item-code sw-mono">${esc(d.itemCode)}</span>
      </div>
      <div class="sw-item-name">${esc(d.itemName)}</div>
    </section>

    <section class="sw-grid">
      <div class="sw-cell sw-span12">
        <span class="sw-cap">Müşteri</span>
        <div class="sw-cell-v">${esc(d.customerName)}</div>
      </div>
      <div class="sw-cell">
        <span class="sw-cap">Üretilen Miktar</span>
        <div class="sw-cell-v sw-mono">${qtyStr}<span class="sw-uom">${esc(d.uom)}</span></div>
      </div>
      <div class="sw-cell">
        <span class="sw-cap">Üretim Hattı</span>
        <div class="sw-cell-v">${esc(d.locationName)}</div>
      </div>
      <div class="sw-cell">
        <span class="sw-cap">Üretim Saati</span>
        <div class="sw-cell-v sw-mono">${timeStr}</div>
      </div>
    </section>

    <section class="sw-lower">
      <div class="sw-bc">
        <div class="sw-bc-head">
          <span class="sw-cap">LOT No</span>
          <span class="sw-bc-lot-top sw-mono">${lotTop}</span>
        </div>
        <div class="sw-bc-body">
          ${barcodeBlock}
        </div>
      </div>

      <div class="sw-spacer"></div>

      <div class="sw-sign">
        <div class="sw-sign-box">
          <div class="sw-sign-line">
            <div class="sw-sign-role">Üretim Sorumlusu</div>
            <div class="sw-sign-hint">Ad Soyad · İmza</div>
          </div>
        </div>
        <div class="sw-sign-box">
          <div class="sw-sign-line">
            <div class="sw-sign-role">Depo Sorumlusu</div>
            <div class="sw-sign-hint">Ad Soyad · İmza</div>
          </div>
        </div>
      </div>
    </section>

    <footer class="sw-foot">
      <span>Bu etiket STEPERP üretim takip sistemi tarafından oluşturulmuştur</span>
      <span class="sw-foot-code sw-mono">${esc(d.batchCode)}</span>
    </footer>
  </div>

  <script>window.onload = function () { window.focus(); window.print(); };</script>
</body>
</html>`;
}

/** Etiketi yeni pencerede açar ve yazdırma diyalogunu tetikler. Popup engellendiyse false döner. */
export function printBatchLabel(d: BatchLabelData): boolean {
  const w = window.open("", "_blank", "width=820,height=1160");
  if (!w) return false;
  w.document.open();
  w.document.write(buildBatchLabelHtml(d));
  w.document.close();
  return true;
}
